import {
    Dialog,
    DialogTrigger,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogDescription
} from "@/components/ui/dialog";
import {Button} from "@/components/ui/button";
import {BadgeDollarSign, Coins, UserPlus, Users} from "lucide-react";
import {Table, TableBody, TableCell, TableHead, TableHeader, TableRow} from "@/components/ui/table.tsx";
import {Badge} from "@/components/ui/badge.tsx";
import React, {useState} from "react";
import {useQuery} from "@tanstack/react-query";
import {Clan} from "@/types/Clan.ts";
import {Session} from "@supabase/supabase-js";
import {BiExit} from "react-icons/bi";
import {ConfirmModal} from "@/components/ConfirmModal.tsx";

const ClanDetailModal = ({clanId, clanDetails, open, setOpen, onApply, isApplied, userSession, userClanApplication, showClanDetails,clanDetailLoading,onLeave,canApply}: {
    clanId: string,
    clanDetails: Clan,
    open: boolean,
    setOpen: React.Dispatch<React.SetStateAction<boolean>>,
    onApply: (clanId: string) => void,
    isApplied: boolean,
    userSession: Session | null,
    userClanApplication: (clanId: string) => void,
    showClanDetails: (clanId: string) => void,
    clanDetailLoading: boolean,
    onLeave: (clanId: string) => void,
    canApply: boolean
}) => {
    const [confirmOpen, setConfirmOpen] = useState(false)

    const handleLeave = () => {
        if (!clanDetails?.id) return
        onLeave(clanDetails.id)
        setConfirmOpen(false)
    }
    const user = clanDetails?.topMembers?.find((member) => member.userId == userSession?.user?.id);
    /*const {data: clanApplication} = useQuery({
        queryKey: ['clanApplication'],
        queryFn: async () => {
            const response = await fetch(`${import.meta.env.VITE_API_URL}/clans/${clan.id}/apply`, {
                method: 'GET',
                headers: {
                    "Authorization": `Bearer ${userSession.access_token}`
                }
            });
            if (!response.ok) {
                throw new Error('Network response was not ok');
            }
            const responseData = await response.json();
            // console.log("category response:", responseData)
            return responseData;
        }
    });*/

    return (
        clanId && (<Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
                <Button variant="default" onClick={(event) => {
                    event.preventDefault()
                    userClanApplication(clanId)
                    showClanDetails(clanId)
                }}>
                    <UserPlus className="h-4 w-4 mr-2"/>
                    View Clan
                </Button>
            </DialogTrigger>

            <DialogContent onCloseAutoFocus={(e) => e.preventDefault()}
                           className="max-w-3xl w-[calc(100vw-2rem)] max-h-[calc(100vh-2rem)] overflow-hidden flex flex-col">
                <DialogHeader>
                    <DialogTitle>{clanDetails?.name}</DialogTitle>
                    <DialogDescription>{clanDetails?.description}</DialogDescription>
                </DialogHeader>

                <div className="mt-4 flex flex-col gap-2 text-sm text-muted-foreground">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                            <Users className="w-4 h-4"/>
                            <span>Members</span>
                        </div>
                        <span className="font-medium">{clanDetails?.memberCount}</span>
                    </div>
                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                            <BadgeDollarSign className="w-4 h-4"/>
                            <span>Total Points</span>
                        </div>
                        <span className="font-medium">{clanDetails?.totalPollPoints ?? 0}</span>
                    </div>
                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                            <Coins className="w-4 h-4"/>
                            <span>Min Points to Apply</span>
                        </div>
                        <span className="font-medium">{clanDetails?.joinCriteria?.value}</span>
                    </div>
                </div>

                <div className="mt-4">
                    <h4 className="font-semibold text-base mb-2">Members</h4>
                    <div className="h-[50vh] w-full overflow-y-auto rounded-md border dark:bg-[#1F2937]">
                        <Table>
                            <TableHeader>
                                <TableRow>
                                    <TableHead className="px-4 py-2 text-center">Username</TableHead>
                                    <TableHead className="px-4 py-2 text-center">Total bp</TableHead>
                                    <TableHead className="px-4 py-2 text-center">Success Rate</TableHead>
                                    <TableHead className="px-4 py-2 text-center">Role</TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {clanDetails?.topMembers.sort((a, b) => a.role == 'owner' ? 0 : 1).map((user) => (
                                    <TableRow key={user.userId} className="border-t">
                                        <TableCell className="px-4 py-2 text-center">@{user.username}</TableCell>
                                        <TableCell className="px-4 py-2 text-center">{user.points}</TableCell>
                                        <TableCell className="px-4 py-2 text-center">{user.correctAnswers}</TableCell>
                                        <TableCell className="px-4 py-2 text-center">

                                            <Badge
                                                key={user.userId}
                                                variant={user.role == 'owner' ? "default" : "secondary"}
                                                className={`text-white mx-auto bg dark:border-gray-600`}

                                            >
                                                {user.role}
                                            </Badge>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </div>
                </div>
                {(user && user?.role == 'member') ? (
                    <div className="mt-6 flex justify-end">
                        <Button onClick={() => {
                            setConfirmOpen(true);
                            // onLeave(clanDetails?.id)
                        }} variant="outline" className="text-white hover:text-white px-6 bg-red-600 hover:bg-red-600/90 dark:bg-red-500 dark:hover:bg-red-900" disabled={isApplied}>
                            <BiExit className="h-4 w-4 mr-2"/> Leave Clan
                        </Button>
                    </div>
                ) : user?.role == 'owner' ? (
                    <></>
                    ) : canApply ? (
                    <div className=" flex justify-end">
                        <Button onClick={() => {
                            onApply(clanDetails?.id)
                        }} variant="amber" className="px-6" disabled={isApplied}>
                            <UserPlus className="h-4 w-4 mr-2"/> Apply to Join
                        </Button>
                    </div>
                ): (
                    <></>
                )}
                {/*{!clanDetails?.topMembers?.find((member) => member.userId == userSession?.user?.id) && (
                    <div className="mt-6 flex justify-end">
                        <Button onClick={() => {
                            onApply(clanDetails?.id)
                        }} variant="amber" className="px-6" disabled={isApplied}>
                        <UserPlus className="h-4 w-4 mr-2"/> Apply to Join
                        </Button>
                    </div>)}*/}
                <ConfirmModal
                    open={confirmOpen}
                    onCancel={() => setConfirmOpen(false)}
                    onConfirm={handleLeave}
                    title="Are you sure you want to leave this clan?"
                    message="You will lose your position and may not be able to rejoin immediately."
                />
            </DialogContent>
        </Dialog>)


    );
};
export default ClanDetailModal;
